import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNotifications } from '@/features/notifications';
import {
    useGetEmailPreferencesQuery,
    useCreateEmailPreferencesMutation,
    useUpdateEmailPreferencesMutation,
} from '@/features/users/api';

import { EmailPreferenceField, EmailPreferencesTransformer } from '@/features/users/api/types';

type UseEmailPreferencesOptions = {
    userId: string;
    showSuccessToast?: boolean;
    showErrorToast?: boolean;
};

type UseEmailPreferencesReturn = {
    preferences: EmailPreferencesTransformer | null | undefined;
    isLoading: boolean;
    isInitializing: boolean;
    isUpdating: boolean;
    updatingField: EmailPreferenceField | null;
    updatePreference: (field: EmailPreferenceField, value: boolean) => Promise<void>;
    refetch: () => void;
};

export function useEmailPreferences({
    userId,
    showSuccessToast = true,
    showErrorToast = true,
}: UseEmailPreferencesOptions): UseEmailPreferencesReturn {
    const { t } = useTranslation(['settings']);
    const { add } = useNotifications();

    const [updatingField, setUpdatingField] = useState<EmailPreferenceField | null>(null);

    // Query para obtener las preferencias
    const {
        data: preferences,
        isLoading: isLoadingQuery,
        isFetching,
        refetch,
    } = useGetEmailPreferencesQuery(userId, {
        skip: !userId,
    });

    // Mutations
    const [createPreferences, { isLoading: isCreating }] = useCreateEmailPreferencesMutation();
    const [updatePreferences, { isLoading: isUpdating }] = useUpdateEmailPreferencesMutation();

    // Crear preferencias iniciales si no existen
    useEffect(() => {
        const initializePreferences = async () => {
            // Si ya existen preferencias o aún está cargando, no hacer nada
            if (preferences !== null || isLoadingQuery || !userId) {
                return;
            }

            // Si preferences es null (no existen), crear registro inicial
            try {
                await createPreferences({
                    user: userId,
                    recommendations_by_interests: true,
                    recommendations_by_activity: true,
                    newsletter_mkt: true,
                }).unwrap();
            } catch (error) {
                console.error('Error creating initial preferences:', error);
                if (showErrorToast) {
                    add({
                        id: new Date().toString(),
                        title: t('ERROR'),
                        description: t('Could not initialize email preferences'),
                    });
                }
            }
        };

        initializePreferences();
    }, [preferences, isLoadingQuery, userId, createPreferences, add, t, showErrorToast]);

    // Función para actualizar una preferencia específica
    const updatePreference = useCallback(
        async (field: EmailPreferenceField, value: boolean) => {
            if (!preferences?.id) {
                console.error('Cannot update preferences: preferences not loaded');
                return;
            }

            try {
                setUpdatingField(field);

                await updatePreferences({
                    id: preferences.id,
                    userId,
                    data: { [field]: value },
                }).unwrap();

                if (showSuccessToast) {
                    add({
                        id: new Date().toString(),
                        title: t('Preference updated'),
                        description: t('Your email preferences have been saved'),
                    });
                }
            } catch (error) {
                console.error('Error updating preference:', error);
                if (showErrorToast) {
                    add({
                        id: new Date().toString(),
                        title: t('ERROR'),
                        description: t('Could not update preference. Please try again.'),
                    });
                }
                // Re-lanzar el error para que el componente pueda manejarlo si lo necesita
                throw error;
            } finally {
                setUpdatingField(null);
            }
        },
        [preferences, userId, updatePreferences, add, t, showSuccessToast, showErrorToast]
    );

    const handleRefetch = useCallback(async () => {
        await refetch();
    }, [refetch]);


    return {
        preferences: preferences ?? null,
        isLoading: isLoadingQuery || isFetching,
        isInitializing: isCreating,
        isUpdating,
        updatingField,
        updatePreference,
        refetch: handleRefetch,
    };
}
